import React, { useEffect, useState, useCallback, useMemo } from "react";
import { X, Loader2, Undo2, Inbox, Trash2, DoorClosed, AppWindow, Home, ChevronDown, ChevronRight, Search } from "lucide-react";
import { listTombstones, restoreTombstone, getDesign } from "../lib/api";
import { toast } from "sonner";

/**
 * V2.0.25 — Recently Deleted dialog.
 *
 * Lists every tombstoned element on the current design (doors / windows /
 * rooms / furniture / floor holes) grouped by kind, with a one-click
 * Restore button per row. Each restore POSTs to
 * `/api/designs/{id}/tombstones/restore` and then refetches the canonical
 * design payload through `onRestored(newDesign)` so the parent's blueprint
 * + chat + 3D view all stay in sync.
 *
 * Why surface tombstones at all? V2.0.24 made tombstones bulletproof on
 * the data layer, but invisible to the user — they were just "gone".
 * This dialog turns the audit-grade record of delete intent into a
 * safety net users can act on.
 */
const KIND_META = {
  doors: { label: "Doors", Icon: DoorClosed },
  windows: { label: "Windows", Icon: AppWindow },
  rooms: { label: "Rooms", Icon: Home },
  furniture: { label: "Furniture", Icon: Inbox },
  floor_holes: { label: "Floor Cutouts", Icon: Trash2 },
};

/**
 * V2.0.26 — Render an ISO timestamp as a friendly relative time
 * ("just now" / "2 minutes ago" / "yesterday" / "Mar 4"). Returns
 * `null` for missing / invalid input so the caller can decide what to
 * show in the empty case.
 */
function formatRelativeTime(iso) {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  const diffSec = Math.floor((Date.now() - t) / 1000);
  if (diffSec < 5) return "just now";
  if (diffSec < 60) return `${diffSec} sec ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hr ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay === 1) return "yesterday";
  if (diffDay < 7) return `${diffDay} days ago`;
  if (diffDay < 30) return `${Math.floor(diffDay / 7)} wk ago`;
  return new Date(t).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: diffDay > 365 ? "numeric" : undefined,
  });
}

export default function RecentlyDeletedDialog({ open, onClose, designId, onRestored }) {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [restoringId, setRestoringId] = useState(null);
  // V2.0.27 — search query for filtering by label / id substring.
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState({ doors: true, windows: true, rooms: true, furniture: true, floor_holes: true });

  const load = useCallback(async () => {
    if (!designId) return;
    setLoading(true);
    try {
      const r = await listTombstones(designId);
      setRows(r.data || []);
    } catch (e) {
      toast.error("Could not load deleted items.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [designId]);

  useEffect(() => {
    if (open) {
      setQuery("");
      load();
    }
  }, [open, load]);

  const handleRestore = async (row) => {
    setRestoringId(row.id);
    try {
      await restoreTombstone(designId, {
        id: row.id,
        kind: row.kind,
        floor_index: row.floor_index,
      });
      toast.success(`Restored: ${row.label}`);
      // Refresh the canonical design so the parent's blueprint reflects
      // the restored element.
      try {
        const fresh = await getDesign(designId);
        onRestored?.(fresh.data);
      } catch {
        // Parent will see the update on its next fetch — non-blocking.
      }
      setRows((prev) => prev.filter((r) => !(r.id === row.id && r.kind === row.kind && r.floor_index === row.floor_index)));
    } catch (e) {
      toast.error("Could not restore. Try again.");
    } finally {
      setRestoringId(null);
    }
  };

  // V2.0.27 — case-insensitive substring filter on label OR id. Empty
  // query keeps every row. Declared BEFORE the conditional early-return
  // so the hook is called in the same order on every render.
  const filteredRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        (r.label || "").toLowerCase().includes(q) ||
        (r.id || "").toLowerCase().includes(q),
    );
  }, [rows, query]);

  if (!open) return null;

  const grouped = filteredRows.reduce((acc, r) => {
    (acc[r.kind] = acc[r.kind] || []).push(r);
    return acc;
  }, {});
  // V2.0.26 — Preserve the backend's newest-first order inside each group.
  Object.values(grouped).forEach((arr) =>
    arr.sort((a, b) => {
      if (a.deleted_at && b.deleted_at) return b.deleted_at.localeCompare(a.deleted_at);
      if (a.deleted_at) return -1;
      if (b.deleted_at) return 1;
      return 0;
    }),
  );

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm grid place-items-center p-4"
      onClick={onClose}
      data-testid="recently-deleted-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="recently-deleted-dialog"
        className="bg-zinc-950 border border-zinc-800 w-full max-w-2xl max-h-[85vh] flex flex-col"
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-800">
          <div className="flex items-center gap-2">
            <Undo2 className="w-4 h-4 text-brass-400" />
            <h2
              data-testid="recently-deleted-title"
              className="font-mono text-sm text-brass-300 uppercase tracking-widest"
            >
              // Recently Deleted
            </h2>
            <span
              data-testid="recently-deleted-count"
              className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 ml-1"
            >
              {query.trim()
                ? `${filteredRows.length} of ${rows.length}`
                : `${rows.length} item${rows.length === 1 ? "" : "s"}`}
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="recently-deleted-close-btn"
            className="text-zinc-500 hover:text-zinc-100"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* V2.0.27 — Search box. Hidden when there are 0 tombstones to
            keep the empty state clean. */}
        {rows.length > 0 && (
          <div
            data-testid="recently-deleted-search-bar"
            className="px-5 py-2.5 border-b border-zinc-800 flex items-center gap-2"
          >
            <Search className="w-3.5 h-3.5 text-zinc-500 shrink-0" />
            <input
              type="text"
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search deleted items by name or id…"
              data-testid="recently-deleted-search-input"
              className="bg-transparent border-0 outline-none flex-1 text-sm text-zinc-100 placeholder:text-zinc-600 focus:placeholder:text-zinc-500"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                data-testid="recently-deleted-search-clear"
                className="text-zinc-500 hover:text-zinc-100"
                title="Clear search"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading && (
            <div
              data-testid="recently-deleted-loading"
              className="grid place-items-center py-12 text-zinc-500 font-mono text-xs"
            >
              <Loader2 className="w-5 h-5 animate-spin text-brass-400 mb-2" />
              Loading deleted items…
            </div>
          )}

          {!loading && rows.length === 0 && (
            <div
              data-testid="recently-deleted-empty"
              className="grid place-items-center py-12 text-center"
            >
              <Inbox className="w-7 h-7 text-zinc-600 mb-3" />
              <p className="font-mono text-xs uppercase tracking-widest text-zinc-400 mb-1">
                Nothing deleted
              </p>
              <p className="text-sm text-zinc-500 max-w-sm">
                Anything you delete in the editor (doors, windows, rooms,
                furniture) will appear here so you can restore it with one click.
              </p>
            </div>
          )}

          {/* V2.0.27 — Empty state when a search yields no matches. */}
          {!loading && rows.length > 0 && filteredRows.length === 0 && (
            <div
              data-testid="recently-deleted-no-search-results"
              className="grid place-items-center py-12 text-center"
            >
              <Search className="w-7 h-7 text-zinc-600 mb-3" />
              <p className="font-mono text-xs uppercase tracking-widest text-zinc-400 mb-1">
                No matches
              </p>
              <p className="text-sm text-zinc-500 max-w-sm">
                Nothing in your deleted items matched&nbsp;
                <span className="text-zinc-300 font-mono">&ldquo;{query}&rdquo;</span>.
                Try a shorter search or clear it to see everything.
              </p>
            </div>
          )}

          {!loading && filteredRows.length > 0 && (
            <div className="space-y-4">
              <p className="text-xs text-zinc-400 leading-relaxed border-l-2 border-brass-500/40 pl-3">
                These items were deleted from the floor plan. They&apos;re hidden
                from the 2D / 3D views but kept as tombstones to stop the AI from
                resurrecting them. Click Restore to bring any back.
              </p>
              {Object.entries(grouped).map(([kind, items]) => {
                const meta = KIND_META[kind] || { label: kind, Icon: Inbox };
                const Icon = meta.Icon;
                const isOpen = expanded[kind] !== false;
                return (
                  <div
                    key={kind}
                    data-testid={`recently-deleted-group-${kind}`}
                    className="border border-zinc-800"
                  >
                    <button
                      type="button"
                      onClick={() =>
                        setExpanded((p) => ({ ...p, [kind]: !isOpen }))
                      }
                      className="w-full flex items-center justify-between px-3 py-2 bg-zinc-900 hover:bg-zinc-900/60 transition-colors"
                      data-testid={`recently-deleted-group-toggle-${kind}`}
                    >
                      <div className="flex items-center gap-2">
                        {isOpen ? (
                          <ChevronDown className="w-3 h-3 text-zinc-500" />
                        ) : (
                          <ChevronRight className="w-3 h-3 text-zinc-500" />
                        )}
                        <Icon className="w-3.5 h-3.5 text-brass-400" />
                        <span className="font-mono text-[11px] uppercase tracking-widest text-zinc-200">
                          {meta.label}
                        </span>
                      </div>
                      <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                        {items.length}
                      </span>
                    </button>
                    {isOpen && (
                      <div className="divide-y divide-zinc-900">
                        {items.map((row) => (
                          <div
                            key={`${row.kind}-${row.floor_index}-${row.id}`}
                            data-testid={`recently-deleted-row-${row.id}`}
                            className="flex items-center justify-between px-3 py-2.5 gap-3 hover:bg-zinc-900/40"
                          >
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-sm text-zinc-100 truncate">
                                  {row.label}
                                </span>
                                {formatRelativeTime(row.deleted_at) && (
                                  <span
                                    data-testid={`recently-deleted-time-${row.id}`}
                                    title={row.deleted_at}
                                    className="font-mono text-[9px] uppercase tracking-widest text-brass-400 px-1.5 py-0.5 border border-brass-700/40 bg-brass-950/30"
                                  >
                                    {formatRelativeTime(row.deleted_at)}
                                  </span>
                                )}
                              </div>
                              <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">
                                Floor {row.floor_index} · id {row.id.slice(0, 24)}
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleRestore(row)}
                              disabled={restoringId === row.id}
                              data-testid={`recently-deleted-restore-${row.id}`}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-brass-700 text-brass-300 hover:bg-brass-950/50 disabled:opacity-40 disabled:cursor-not-allowed font-mono text-[10px] uppercase tracking-widest shrink-0"
                            >
                              {restoringId === row.id ? (
                                <>
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                  Restoring
                                </>
                              ) : (
                                <>
                                  <Undo2 className="w-3 h-3" />
                                  Restore
                                </>
                              )}
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
